import React, { useState, useEffect } from 'react';
import { BookOpen, Search, Star, Tag, TrendingUp, Filter, Eye, FileText, HelpCircle, AlertCircle } from 'lucide-react';
import { knowledgeBaseService } from '../../services/knowledgeBaseService';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';


export default function KnowledgeBaseHub() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedType]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = {};
      if (selectedCategory) filters.categoryId = selectedCategory;
      if (selectedType) filters.articleType = selectedType;

      const [articlesData, categoriesData, popularData] = await Promise.all([
        knowledgeBaseService?.getArticles(filters),
        knowledgeBaseService?.getCategories(),
        knowledgeBaseService?.getPopularArticles(5)
      ]);

      setArticles(articlesData);
      setCategories(categoriesData);
      setFeaturedArticles(articlesData?.filter(a => a?.isFeatured));
      setPopularArticles(popularData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery?.trim()) {
      loadData();
      return;
    }

    try {
      setLoading(true);
      const results = await knowledgeBaseService?.searchArticles(searchQuery);
      setArticles(results);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      faq: HelpCircle,
      tutorial: BookOpen,
      policy: FileText,
      troubleshooting: AlertCircle,
      guide: BookOpen,
      reference: FileText
    };
    const Icon = icons?.[type] || BookOpen;
    return <Icon className="w-5 h-5" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      faq: 'bg-blue-100 text-blue-800',
      tutorial: 'bg-green-100 text-green-800',
      policy: 'bg-purple-100 text-purple-800',
      troubleshooting: 'bg-red-100 text-red-800',
      guide: 'bg-indigo-100 text-indigo-800',
      reference: 'bg-gray-100 text-gray-800'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center space-x-3 mb-6">
            <BookOpen className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Knowledge Base</h1>
              <p className="text-indigo-100 mt-1">Find answers and learn about our platform</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles, guides, and documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                className="w-full pl-12 pr-4 py-4 rounded-lg border-2 border-transparent focus:border-white focus:ring-2 focus:ring-white focus:ring-opacity-50 text-gray-900 placeholder-gray-500"
              />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {/* Featured Articles Carousel */}
        {featuredArticles?.length > 0 && !searchQuery && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Star className="w-6 h-6 text-yellow-500 fill-current" />
              <h2 className="text-2xl font-bold text-gray-900">Featured Articles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles?.slice(0, 3)?.map(article => (
                <div
                  key={article?.id}
                  onClick={() => navigate(`/knowledge-base/${article?.slug}`)}
                  className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border-2 border-indigo-200 hover:border-indigo-400 cursor-pointer transition-all hover:shadow-lg"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${getTypeColor(article?.articleType)}`}>
                      {getTypeIcon(article?.articleType)}
                    </div>
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{article?.title}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{article?.excerpt}</p>
                  {article?.category && (
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span className="px-2 py-1 bg-white rounded-full">{article?.category?.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Categories */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                Categories
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    !selectedCategory
                      ? 'bg-indigo-50 text-indigo-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Categories
                </button>
                {categories?.map(cat => (
                  <button
                    key={cat?.id}
                    onClick={() => setSelectedCategory(cat?.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === cat?.id
                        ? 'bg-indigo-50 text-indigo-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {cat?.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Article Types */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Article Type</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedType(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    !selectedType
                      ? 'bg-indigo-50 text-indigo-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Types
                </button>
                {['faq', 'tutorial', 'guide', 'policy', 'troubleshooting', 'reference']?.map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors capitalize ${
                      selectedType === type
                        ? 'bg-indigo-50 text-indigo-700 font-medium' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Articles */}
            {popularArticles?.length > 0 && !searchQuery && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Popular Articles
                </h3>
                <div className="space-y-3">
                  {popularArticles?.map(article => (
                    <div
                      key={article?.id}
                      onClick={() => navigate(`/knowledge-base/${article?.slug}`)}
                      className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                        {article?.title}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Eye className="w-3 h-3 mr-1" />
                        {article?.viewCount} views
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : articles?.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articles?.map(article => (
                  <div
                    key={article?.id}
                    onClick={() => navigate(`/knowledge-base/${article?.slug}`)}
                    className="bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(article?.articleType)}`}>
                          <div className="flex items-center space-x-1">
                            {getTypeIcon(article?.articleType)}
                            <span className="capitalize">{article?.articleType}</span>
                          </div>
                        </div>
                        {article?.isFeatured && <Star className="w-5 h-5 text-yellow-500 fill-current" />}
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {article?.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">{article?.excerpt}</p>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        {article?.category && (
                          <span className="px-2 py-1 bg-gray-100 rounded-full">{article?.category?.name}</span>
                        )}
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <Eye className="w-3 h-3 mr-1" />
                            {article?.viewCount}
                          </div>
                          {article?.tags?.length > 0 && (
                            <div className="flex items-center">
                              <Tag className="w-3 h-3 mr-1" />
                              {article?.tags?.length}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}